import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { deleteEmployee, getEmployees } from '../../../services/employees';
import { onMutateSuccess, onQuerySuccess } from '../../../utils/queryResponse';
import SetEmployeeModal from './employeeSet';
import OverlayLoader from '../../../components/overlayLoader';
import { SkeletonRows } from '../../../components/skeleton';
import ConfirmationModal from '../../../components/modals/confirmation';
import Avatar from '../../../components/avatar';

const columns = ['firstName', 'lastName']

const PencilIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);

const EmployeesPage = () => {
    const { t } = useTranslation();

    const [setEmployeeModal, setSetEmployeeModal] = useState(false)
    const [confirmationModal, setConfirmationModal] = useState(false)

    const [employees, setEmployees] = useState([])
    const [selectedEmployee, setSelectedEmployee] = useState()

    const { data, isFetching } = useQuery('employees', getEmployees)

    const queryClient = useQueryClient()
    const { mutate, isLoading: isDeleting } = useMutation(deleteEmployee, {
        onSuccess: onMutateSuccess((result) => {
            queryClient.invalidateQueries(['employees'])
        })
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setEmployees(result)
        })
    }, [data])

    const handleSetEmployee = (employee) => {
        setSelectedEmployee(employee)
        setSetEmployeeModal(true)
    }

    const handleConfirmation = (employee) => {
        setSelectedEmployee(employee)
        setConfirmationModal(true)
    }

    const handleDeleteEmployee = () => {
        mutate(selectedEmployee.id)
        setSelectedEmployee()
    }

    return (
        <div className="sbr">
            <SetEmployeeModal display={setEmployeeModal} toggle={setSetEmployeeModal} employeeId={selectedEmployee?.id ?? ''} />
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('settingsPage.deleteEmployee')}
                message={t('settingsPage.confirmDeleteEmployee')} confirmAction={handleDeleteEmployee} />

            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('settingsPage.employees')}</div>
                    <div className="sub">{t('settingsPage.employeesPageDesc')}</div>
                </div>
                <div className="act">
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleSetEmployee()}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('settingsPage.addEmployee')}
                    </button>
                </div>
            </div>

            {/* Employee list */}
            <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '6px 16px', position: 'relative', minHeight: '80px' }}>
                    {employees.length === 0 && isFetching ? (
                        <div style={{ padding: '16px 8px' }}>
                            <SkeletonRows rows={5} widths={['18%', '34%', '34%']} />
                        </div>
                    ) : employees.length === 0 ? (
                        <div className="sbr-empty">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                            </svg>
                            {t('settingsPage.noEmployees')}
                        </div>
                    ) : (
                        employees.map((employee, idx) => (
                            <div key={employee.id ?? idx} className="row-sbr">
                                <Avatar className="a" size={38}
                                    name={`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                                    src={employee?.avatarUrl ?? employee?.user?.avatarUrl} />
                                <div className="info">
                                    <div className="nm">{employee.firstName} {employee.lastName}</div>
                                </div>
                                <div className="rt" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button title={t('common.edit')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                        style={{ padding: '6px 8px', color: 'var(--brand)' }}
                                        onClick={() => handleSetEmployee(employee)}>
                                        <PencilIcon />
                                    </button>
                                    <button title={t('common.delete')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                        style={{ padding: '6px 8px', color: 'var(--bad)' }}
                                        onClick={() => handleConfirmation(employee)}>
                                        <TrashIcon />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                    {((isFetching && employees.length > 0) || isDeleting) && <OverlayLoader />}
                </div>
            </div>
        </div>
    );
};

export default EmployeesPage;
